import * as React from 'react';
import { H3 } from '../../designSystem/typography/Heading';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { ActivityDetailsPanel } from './common/ActivityDetailsPanel';
import { Activity, LocationInspectionPointList, PublicAmorgUser, SafetyInspection } from '../../amorg-common';
import { createAllLocationInspections, getAvailableLocationInspectionPoints, getSafetyInspection } from '../../api/safetyInspectionClientApi';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { getLocations } from '../../api/locationClientApi';
import { Icon } from '../../designSystem/icons/Icons';
import { CollapsiblePanel } from '../../designSystem/collapsible/CollapsiblePanel';
import { ActivityHeader } from './common/ActivityHeader';
import { Divider } from '../../designSystem/Divider';
import { TitleContainer } from './common/AgendaMeetingActivityView';
import { getActivity, updateActivity } from '../../api/activityClientApi';
import { LocationPanel } from './SafetyInspection/pageView/LocationPanel';
import { ListRow } from '../../designSystem/compositeComponents/ListRow';
import { Button } from '../../designSystem/button/Button';
import { P } from '../../designSystem/typography/Body';
import { getUsers } from '../../api/userClientApi';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { Card } from '../../designSystem/Card';
import { Redirect } from 'react-router-dom';
import { DeviceView } from '../../designSystem/DeviceView';
import { ReferenceFilesPanelCollapsible } from '../../designSystem/compositeComponents/ReferenceFilesPanel';


export type SafetyInspectionPageViewProps =
    {
        activityId: string;
    };
export default function SafetyInspectionPageView (props: SafetyInspectionPageViewProps): JSX.Element 
{
    const [inspection, setInspection] = React.useState<SafetyInspection>(null);
    const [availableInspections, setAvailableInspections] = React.useState<LocationInspectionPointList[]>(null);
    const [expandedLocationIds, setExpandedLocationIds] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [activity, setActivity] = React.useState<Activity>(null);
    const [users, setUsers] = React.useState<PublicAmorgUser[]>([]);
    const [inspectionPointRedirect, setInspectionPointRedirect] = React.useState(false);

    React.useEffect(() =>
    {
        const load = async () =>
        {
            setLoading(true);
            const act = await getActivity(props.activityId);
            setActivity(act);
            setUsers(await getUsers());
            setInspection(await getSafetyInspection(act.id));
            setAvailableInspections(await getAvailableLocationInspectionPoints());

            const locations = await getLocations();
            setExpandedLocationIds(locations.map(l => l.id));
            setInspection(await getSafetyInspection(props.activityId));
            setLoading(false);
        };
        load();
    }, [props.activityId]);


    async function handleClickCreateInspections (locationId: string)
    {
        setLoading(true);

        await createAllLocationInspections(activity.id, locationId);
        setInspection(await getSafetyInspection(activity.id));

        setLoading(false);
    }

    function handleToggleExpand (locationId: string, expanded: boolean)
    {
        setExpandedLocationIds(
            expanded
                ? [...expandedLocationIds, locationId]
                : expandedLocationIds.filter(id => id !== locationId)
        );
    }

    async function handleUpdate ()
    {
        setLoading(true);
        const act = await getActivity(props.activityId);
        setActivity(act);
        setUsers(await getUsers());
        setInspection(await getSafetyInspection(activity.id));
        setAvailableInspections(await getAvailableLocationInspectionPoints());
        setLoading(false);
    }

    const locationsWithInspections = inspection?.locations.filter(l => l.facilities.some(f => f.inspectionPoints.length > 0)) || [];
    let availableLocations = [];
    if (availableInspections && inspection)
        availableLocations = inspection.locations.filter(l =>
            l.facilities.every(f => f.inspectionPoints.length === 0)
            && availableInspections.some(al => al.locationId === l.locationId && al.facilities.some(f => f.inspectionPoints.length > 0))
        ) || [];

    async function updateTitle (value: string)
    {
        await updateActivity(activity.id, value, activity.description, activity.start, activity.end, activity.location);
        handleUpdate();
    }

    const sharedComponent = 
    activity &&
    <CollapsiblePanel
        defaultOpen={true}
        openHeader={
            <TitleContainer title={activity.title} updateTitle={updateTitle} />
        }
        closedOrDefaultHeader={
            <TitleContainer title={activity.title} updateTitle={updateTitle} />
        }>
            <Card variant="outlined">
                <ActivityDetailsPanel
                    activity={activity}
                    descriptionText="Beskrivelse"
                    periodSelection='date-time'
                    onActivityUpdated={handleUpdate}
                    componentTitle='Møde info'
                    users={users}
                    infoComponents={<></>}
                    participantTexts={{ header: 'Ansvarlige', editButton: 'Rediger ansvarlige' }} />

                    <ReferenceFilesPanelCollapsible objectId={activity.id} referenceType="SafetyInspection" />
            </Card>
            
    </CollapsiblePanel>

    return (
        <>
            <LoadingPageOverlay loading={loading} />

            {inspectionPointRedirect &&
                <Redirect to={`/inspectionpoints?safetyInspectionId=${activity.id}/`} />
            }

            {activity &&
                <SingleColumnLayout
                    noDivider
                    contentContainer={
                        <>
                            <ActivityHeader
                                activity={activity}
                                deleteConfirmationTextInModal="Tilhørende handlepunkter vil ligeledes blive slettet."
                                additionalToolBarButtons={[
                                    { variant: 'text', icon:'navigation_settings', tooltip: 'Indstilling af kontrolpunkter', 
                                        onClick: () => { setInspectionPointRedirect(true) } 
                                    },
                                ]} />

                            <DeviceView
                                tablet={sharedComponent}
                                desktop={sharedComponent} />

                            <Flex flexDirection='column' gap={4}>
                                <H3 color="gray">VALGTE LOKATIONER</H3>
                                <Divider />
                                {locationsWithInspections.length === 0 &&
                                    <P>Ingen lokationer tilføjet</P>
                                }
                                <div>
                                    {locationsWithInspections.map(l =>
                                    {
                                        return (
                                            <LocationPanel
                                                key={l.locationId}
                                                activityId={activity.id}
                                                location={l}
                                                expanded={expandedLocationIds.includes(l.locationId)}
                                                onToggleExpand={handleToggleExpand}
                                                onClickCreate={handleClickCreateInspections}
                                                onUpdate={handleUpdate} />
                                        );
                                    })}
                                </div>
                            </Flex>

                            <Flex flexDirection='column' gap={4}>
                                <H3 color="gray">ØVRIGE LOKATIONER</H3>
                                <Divider />
                                {availableLocations.length === 0 && locationsWithInspections.length === 0 &&
                                    <P>Der skal oprettes lokationer for at kunne afvikle sikkerhedsrunderinger</P>
                                }
                                {availableLocations.length === 0 && locationsWithInspections.length > 0 &&
                                    <P>Alle lokationer er tilføjet</P>
                                }
                                {availableLocations.map(l =>
                                    <ListRow key={l.locationId} styleVariant='normal'>
                                        <Flex justifyContent='stretch'>
                                            <Flex gap={16} alignItems='center'>
                                                <Icon name='facility_domain' size='small' />
                                                <H3>{l.name}</H3>
                                            </Flex>
                                            <Flex justifyContent='flex-end' style={{ flexGrow: 1 }}>
                                                <Button variant='filled' text='Tilføj' onClick={() => handleClickCreateInspections(l.locationId)} />
                                            </Flex>
                                        </Flex>
                                    </ListRow>
                                )}
                            </Flex>
                        </>
                    } />
            }
        </>
    );
}